import React from "react"
import * as com from "../../Common"
import "./IneligibleReason.css"

/**
 * A lender usually rejects a whole family of products for one reason — every MH
 * Advantage ladder, every product over a DTI cap. Listing them one per line
 * repeats the same sentence a dozen times, so group by reason and show the
 * products it knocked out instead. First-seen order, so the engine's own
 * ordering still comes through.
 */
export const groupReasonsByCause = (productLevelReasons) => {
    if (!productLevelReasons || productLevelReasons.length === 0) return []
    const order = []
    const byReason = {}
    productLevelReasons.forEach((r) => {
        const reason = String(r.IneligibleReason || "").trim() || "No reason reported."
        if (!byReason[reason]) {
            byReason[reason] = []
            order.push(reason)
        }
        if (r.ProductName) byReason[reason].push(r.ProductName)
    })
    return order.map((reason) => ({ reason, products: byReason[reason] }))
}

const WarningIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
        <path d="M12 3L22 20H2L12 3Z" stroke="#CB8600" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 10V14" stroke="#CB8600" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1" fill="#CB8600" />
    </svg>
)

export const IneligibleLenderCell = ({ record }) => {
    const count = record.ProductLevelReasons ? record.ProductLevelReasons.length : 0
    return (
        <div>
            <div className="ineligible-lender-name">{com.formatLenderDisplayName(record.Lender)}</div>
            {count > 0 && (
                <div className="ineligible-lender-count">
                    {count} product{count === 1 ? "" : "s"} ineligible
                </div>
            )}
        </div>
    )
}

/**
 * Lenders set a lender-level reason like "No Exponential qualified products
 * found" once their qualifiers have rejected everything — it restates the
 * product list rather than adding to it, and reads as a contradiction next to
 * the products shown right below. Suppress it when we have those products;
 * it still shows when it is the only thing we know.
 */
const isNoProductsSummary = (reason) =>
    /^no\b.*\bqualified products found\b/i.test(String(reason || "").trim())

export const IneligibleReasonCell = ({ record }) => {
    const groups = groupReasonsByCause(record.ProductLevelReasons)
    const showLenderProblem =
        record.LenderLevelReason &&
        !(groups.length > 0 && isNoProductsSummary(record.LenderLevelReason))
    return (
        <div className="ineligible-reasons">
            {showLenderProblem && (
                <div className="ineligible-lender-problem">
                    <WarningIcon />
                    <span>{record.LenderLevelReason}</span>
                </div>
            )}
            {groups.map((group, i) => (
                <div className="ineligible-group" key={group.reason + i}>
                    <div className="ineligible-group-reason">{group.reason}</div>
                    <div className="ineligible-group-products">
                        {group.products.map((name, j) => (
                            <span className="ineligible-product-chip" key={name + j}>{name}</span>
                        ))}
                    </div>
                </div>
            ))}
            {!record.LenderLevelReason && groups.length === 0 && (
                <div className="ineligible-empty">No reason reported.</div>
            )}
        </div>
    )
}

// Shared by the PPE result table and the LO rate quote table.
export const ineligibleColumns = [
    {
        title: "Lender",
        dataIndex: "lender",
        key: "lender",
        width: 250,
        className: "ineligibleColumn",
        render: (_, record) => <IneligibleLenderCell record={record} />,
        fixed: true,
    },
    {
        title: "Ineligible reason",
        dataIndex: "reason",
        key: "reason",
        className: "ineligibleColumn",
        render: (_, record) => <IneligibleReasonCell record={record} />,
    },
]
